import { useId, useRef, useState } from 'react';
import { Box, Button, Collapse, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { FINANCIAL_FIELDS, type FinancialFieldDef } from '../utils/verificationFinancials';

const PRIMARY_FIELDS: FinancialFieldDef['field'][] = [
  'copayment',
  'coinsurance',
  'remaining_deductible',
  'remaining_oop_max',
];

export type BenefitInputField = FinancialFieldDef['field'];

interface Props {
  values: Record<string, string>;
  assertedFields: ReadonlySet<BenefitInputField>;
  onChange: (field: BenefitInputField, value: string) => void;
  disabled?: boolean;
  invalidField?: BenefitInputField;
}

/** Only edited fields become assertions; untouched prefills stay omitted. */
export function CoverageBenefitFields({
  values,
  assertedFields,
  onChange,
  disabled = false,
  invalidField,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const detailsRef = useRef<HTMLDivElement>(null);
  const detailsExpanded = expanded || !!(invalidField && !PRIMARY_FIELDS.includes(invalidField));

  const field = ({ field: name, label, prefix }: FinancialFieldDef) => {
    const asserted = assertedFields.has(name);
    const value = values[name] ?? '';
    return (
      <Box key={name} sx={{ minWidth: 0 }}>
        <TextField
          label={label}
          name={`benefit-${name}`}
          error={invalidField === name}
          value={value}
          onChange={event => onChange(name, event.target.value)}
          disabled={disabled}
          fullWidth
          size="small"
          sx={{ '& .MuiFormHelperText-root': { fontSize: 10.5, mx: 0, minHeight: 30 } }}
          type="number"
          slotProps={{
            htmlInput: { min: 0, max: prefix === '%' ? 100 : undefined, step: 'any' },
            input:
              prefix === '$'
                ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                : { endAdornment: <InputAdornment position="end">%</InputAdornment> },
          }}
          helperText={
            asserted
              ? value.trim() === ''
                ? 'The saved value will be cleared.'
                : 'This value will be recorded as manually confirmed.'
              : 'Not included in this update.'
          }
        />
      </Box>
    );
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Benefit details (optional)</Typography>
      <Typography sx={{ fontSize: 12.5 }} color="text.secondary">
        Only values you enter or edit are updated. Untouched fields keep their saved values. Delete
        a field’s contents to remove its saved value. A fixed co-pay can supply a collection
        suggestion. Coinsurance dollars remain undetermined.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {FINANCIAL_FIELDS.filter(def => PRIMARY_FIELDS.includes(def.field)).map(field)}
      </Box>
      <Button
        onClick={() => setExpanded(value => !value)}
        aria-expanded={detailsExpanded}
        aria-controls={detailsId}
        sx={{ alignSelf: 'flex-start' }}
      >
        {detailsExpanded ? 'Fewer benefit details' : 'More benefit details'}
      </Button>
      <Collapse
        in={detailsExpanded}
        id={detailsId}
        ref={detailsRef}
        onEntered={() => {
          if (invalidField)
            detailsRef.current
              ?.querySelector<HTMLInputElement>(`input[name="benefit-${invalidField}"]`)
              ?.focus();
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {FINANCIAL_FIELDS.filter(def => !PRIMARY_FIELDS.includes(def.field)).map(field)}
        </Box>
      </Collapse>
    </Stack>
  );
}
